import React, {
  useEffect,
  useState,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import {
  useParams,
} from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import RegenerateIcon from '@mui/icons-material/Cached';
import {
  useGetCodeSuggestions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  SuggestedCodeProps,
} from 'views/EMR/components/ServiceLookup/SuggestedCodesList';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import SelectableProvider from 'core/context/selectable';
import compact from 'lodash/compact';
import map from 'lodash/map';
import SuggestedCode from 'views/EMR/components/ServiceLookup/SuggestedCodesList/SuggestedCode';
import keyBy from 'lodash/keyBy';
import {
  useMixpanelTrack,
} from 'core/hooks';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import Skeleton from 'core/components/Skeleton';

export default function SuggestedCodesContent({
  saveVisitNote,
  type,
  onChange,
}: SuggestedCodeProps) {
  const {
    watch,
    formState: { dirtyFields },
  } = useFormContext();

  const [loading, setLoading] = useState<boolean>(false);
  const { id = '' } = useParams<{ id: string }>();
  const {
    data: suggestedCodes = [],
    isFetching,
    refetch,
  } = useGetCodeSuggestions(Number(id ?? 0), type);

  const suggestedCodesByCode = keyBy(suggestedCodes, 'code');

  const isAssessmentCode = type === 'ICDCode';
  const codesFormKey = isAssessmentCode ? 'bill.assessmentCodes' : 'bill.serviceCodes';
  const selectedCodes = (watch(codesFormKey) ?? []) as VisitNoteServiceCode[];
  const selectedCodesSet = new Set(selectedCodes.map(({ code }) => code));

  const commonCodes = compact(map(suggestedCodes.filter(({ code }) => selectedCodesSet.has(code)), 'code'));

  const isLoading = loading || isFetching;

  const fetchCodes = async () => {
    setLoading(true);
    const isDirty = isAssessmentCode
      ? (dirtyFields.physicalExam || dirtyFields.additionalHPI)
      : dirtyFields.plan;
    if (isDirty) {
      await saveVisitNote?.();
    }
    await refetch();
    setLoading(false);
  };

  useEffect(() => {
    fetchCodes();
  }, []);
  const mixpanelTrack = useMixpanelTrack();

  return (
    <div className="flex flex-col gap-2 max-h-80 w-80 pb-2">
      <div className="flex items-center justify-between py-1">
        <span className="text-base font-semibold">
          Suggested Codes
        </span>
        <IconButton
          className="!p-0"
          disabled={isLoading}
          onClick={() => {
            refetch();
          }}
        >
          <RegenerateIcon fontSize="small" />
        </IconButton>
      </div>
      {isLoading ? (
        <div className="flex flex-col">
          <Skeleton className="!w-full" />
        </div>
      ) : (
        <SelectableProvider selected={commonCodes ?? []}>
          <div className="flex-1 overflow-y-auto flex flex-col gap-2">
            {suggestedCodes.length === 0 ? (
              <span>No relevant codes found</span>
            ) : (suggestedCodes.map(({ code, description }) => (
              <SuggestedCode
                key={code}
                code={code}
                description={description}
                onChange={(checked) => {
                  onChange(compact([
                    ...(checked
                      ? selectedCodes
                      : selectedCodes.filter(({ code: _code }) => _code !== code)),
                    checked ? suggestedCodesByCode[code ?? ''] : undefined,
                  ]));
                  mixpanelTrack('selectSuggestedCode', { type });
                  visitNoteEventChannel.emit('onServiceCodesUpdate', type);
                }}
              />
            ))
            )}
          </div>
        </SelectableProvider>
      )}
    </div>
  );
}
